import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useScheduledBroadcast } from '../../state/scheduleState';
import { useShare } from '../../utils';
import { Popup } from '../../utils/popups';
import { getStreamUrl } from '../../utils/url';
import { BroadcastNameplate } from '../BroadcastNameplate';
import { DeviceDropdown } from '../DeviceDropdown';
import { InfoIcon as PrestyledInfoIcon, SettingsIcon, ShareIcon, } from '../Icon';
import { PopupButton } from '../PopupButton';
import { Toggle } from '../Toggle';
import { BroadcastName } from '../Ui';
import { Inputs } from './SettingsPopup';
import { UtilityPopup } from './UtilityPopup';
export function BroadcastPopup({ reject: rejectProp, streamId, initialQuality, onVideoQualityChanged, ...props }) {
    const broadcast = useScheduledBroadcast(streamId);
    const { canShare, openSystemShareDialog } = useShare();
    const [quality, setQuality] = useState(initialQuality);
    const [isAudioDropdownOpen, setIsAudioDropdownOpen] = useState(false);
    const [isVideoDropdownOpen, setIsVideoDropdownOpen] = useState(false);
    const reject = useCallback((reason) => {
        if (isAudioDropdownOpen || isVideoDropdownOpen) {
            return;
        }
        rejectProp(reason);
    }, [rejectProp, isAudioDropdownOpen, isVideoDropdownOpen]);
    function isValidQuality(value) {
        return value === 'sd' || value === 'hd';
    }
    return (_jsx(UtilityPopup, { reject: reject, ...props, children: _jsxs(Content, { children: [_jsx(BroadcastName, { children: broadcast?.name || '' }), !!streamId && (_jsxs(_Fragment, { children: [_jsx(BroadcastNameplate, { streamId: streamId }), canShare && (_jsx(Inputs, { children: _jsx(PopupButton, { type: "button", onClick: () => {
                                    openSystemShareDialog(getStreamUrl(streamId, {
                                        broadcastTitle: broadcast?.name,
                                        broadcastStartsAt: broadcast?.startsAt,
                                    }));
                                }, prependComponent: _jsx(ShareIcon, {}), children: "Share link" }) }))] })), _jsxs(Inputs, { children: [_jsx(DeviceDropdown, { kind: "audio", isOpen: isAudioDropdownOpen, setIsOpen: setIsAudioDropdownOpen }), _jsx(DeviceDropdown, { kind: "video", isOpen: isVideoDropdownOpen, setIsOpen: setIsVideoDropdownOpen })] }), _jsxs(VideoSettingsRoot, { children: [_jsx(Segment, { children: _jsxs(VideoSettings, { children: [_jsx(SettingsIcon, {}), _jsx("div", { children: "Video quality" }), _jsx(Toggle, { options: ['SD', 'HD'], selected: quality.toUpperCase(), onChange: (value) => {
                                            const lowerValue = value.toLowerCase();
                                            if (isValidQuality(lowerValue)) {
                                                onVideoQualityChanged?.(lowerValue);
                                                setQuality(lowerValue);
                                            }
                                            else {
                                                console.error('Unexpected video quality value:', value);
                                            }
                                        } })] }) }), _jsxs(InfoSegment, { children: [_jsx(InfoIcon, {}), _jsx(Info, { children: "We suggest choosing SD if you have low upload bandwidth" })] })] })] }) }));
}
const VideoSettings = styled.div `
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.75rem;
`;
const Segment = styled.div `
    align-items: center;
    background-color: rgba(53, 54, 49, 0.55);
    box-sizing: border-box;
    display: flex;
    gap: 1rem;
    min-height: 1.25rem;
    padding: 1rem;
`;
const InfoSegment = styled(Segment) `
    border-top: 0.0625rem solid rgba(222, 222, 222, 0.2);
    gap: 0.625rem;

    & + ${Segment} {
        padding-top: 0;
    }
`;
const InfoIcon = styled(PrestyledInfoIcon) `
    flex-shrink: 0;
`;
const Info = styled.div `
    font-size: 0.875rem;
    line-height: 1.25rem;
`;
const VideoSettingsRoot = styled.div `
    border-radius: 1rem;
    margin-top: 1.25rem;
    color: #ffffff;
    overflow: hidden;
`;
const Content = styled.div `
    padding-top: 1.5rem;

    ${BroadcastName} {
        margin-bottom: 1.25rem;
    }
`;
const broadcastPopup = Popup.create(BroadcastPopup);
export function showBroadcastPopup({ anchorRef, onBeforeOpen, onDismiss, streamId, initialQuality, onVideoQualityChanged, }) {
    (async () => {
        try {
            onBeforeOpen?.();
            await broadcastPopup.pop({
                anchorRef,
                streamId,
                initialQuality,
                onVideoQualityChanged,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    })();
}
